<template lang="pug">
	.schedule-event-rules.flex-col.space.tb-padding
		ListView.full-width(ref="listView",
			:pageSize="10",
			cardCss="event-rule-card",
			initialView="cards",
			:hideViewsSelection="true",
			:getTotal="(d) => d.TotalNumberOfRecords",
			:getItems="(d) => d.Results",
			:getData="loadRules",
			@reload="newRule = null"
		)
			template(v-slot:card="{ data }")
				ScheduleEventRule.full-width.align-center(ref="rules", :ruleId="data.Oid", :name="data.Name", @set-criteria="setCriteria", @change="updateCreateEventRule", @remove="removeEventRule", :icon="getIcon(data)", :isDatasetRule="isDatasetRule(data)")
			template(slot="last-row")
				ScheduleEventRule.full-width.align-center(v-if="newRule", :ruleId="newRule.id", :name="newRule.name", @set-criteria="setCriteria", @change="updateCreateEventRule", :edit="true", @remove="newRule = null", :icon="getIcon(newRule)", :newRule="newRule", :isDatasetRule="isDatasetRule(newRule)")
			.flex-row.space.align-center(slot="left-actions")
				.font-big {{$tCaption('Event rules')}}
				DxDropDownButton(
					:items="ruleTypes",
					:text="$tCaption('Add')",
					icon="fas fa-plus",
					@item-click="onCreateEventRule",
					display-expr="name",
					key-expr="id",
					:drop-down-options="{ width: 230 }",
					styling-mode="filled",
					:focusStateEnabled="false"
				)
			.flex-row.space(slot="right-actions")
				FileUploader(:text="$tCaption('Upload dynamic dataset')", icon="fas fa-cloud-upload-alt", @upload="onFileUpload", @popup="dataSetName = `Data set`")
					template(slot="top-content")
						.flex-col
							.font-small.upper.grey {{$tCaption('Name')}}
							DxTextBox(:width="200", :value.sync="dataSetName")
		Popup(:popupVisible="criteriaPopupVisible")
			template(v-if="currentEventRule")
				StructureCriteria(v-if="currentEventRuleService", :service="currentEventRuleService", :criteria="currentEventRule.criteria", :params="currentEventRule.runParameters", @change="onExternalServiceCriteriaChange")
				FilterBuilder(v-else, :fields="builderFields", v-model="currentEventRule.criteria")
			DxButton(slot="actions-right", :text="$tCaption('Close')", @click="criteriaPopupVisible=false")
</template>

<script>
	import _ from 'lodash';
	import Data from 'app-core/Data';
	import DxTextBox from 'devextreme-vue/text-box';
	import DxButton from 'devextreme-vue/button';
	import FileUploader from 'app-core/FileUploader.vue';
	import Popup from 'app-core/Popup.vue';
	import FilterBuilder from 'app-core/FilterBuilder.vue';
	import ScheduleEventRule from './ScheduleEventRule.vue';
	import ListView from 'app-core/ListView.vue';
	import DxDropDownButton from 'devextreme-vue/drop-down-button';
	import StructureCriteria from 'view/webServiceCaller/StructureCriteria.vue';

	export default {
		translationCategory: 'schedule.ScheduleEventRules',
		name: 'schedule-event-rules',
		components: {
			DxTextBox, DxButton, FileUploader, Popup, FilterBuilder, ScheduleEventRule, ListView, DxDropDownButton, StructureCriteria
		},
		data() {
			if (__DEBUG__)
				window.rules = this;
			return {
				currentEventRule: null,
				currentEventRuleDataSet: null,
				currentEventRuleService: null,
				criteriaPopupVisible: false,
				dataSetName: null,
				ruleTypes: [
					{ id: 'ExternalServiceRule', name: () => this.$tCaption('External service rule'), icon: 'fas fa-cloud-download-alt' },
					{ id: 'DynamicDatasetRule', name: () => this.$tCaption('Dataset-based rule'), icon: 'fas fa-table' },
				],
				newRule: null
			};
		},
		computed: {
			builderDataSet() {
				return this.currentEventRuleDataSet;
			},
			builderFields() {
				if (!this.builderDataSet) return null;
				return this.builderDataSet.columns.map(f => {
					const dataType = f.type === 'Double' ? 'number' : (f.type === 'DateTime' ? 'date' : 'string');
					return {
						caption: f.name,
						dataField: f.name,
						dataType,
						lookup: dataType === 'string' ? {
							dataSource: this.builderDataSet.data.map(d => d[f.name]),
							allowClearing: true
						} : null
					};
				});
			}
		},
		methods: {
			getIcon(data) {
				return this.isDatasetRule(data) ? 'fas fa-table' : 'fas fa-cloud-download-alt';
			},
			isDatasetRule(data) {
				return data && (data.Type || data.type || '').indexOf('DynamicDatasetRule') > -1;
			},
			updateCreateEventRule(rule, callback) {
				if (!rule) {
					rule = {
						id: null,
						name: this.$tCaption('New event rule'),
						_isLoading: false,
						type: 'DynamicDatasetRule'
					};
				}
				var method = 'CreateUpdateDynamicDatasetRule';
				if (rule.type && rule.type.indexOf('DynamicDatasetRule') === -1) {
					method = 'CreateUpdateExternalServiceRule';
				}
				if (__DEBUG__)
					console.debug('Add/update event rule', { rule, method });
				if ((rule.dataSet || rule.externalServiceId) && !rule._isLoading)  {
					rule._isLoading = true;
					var RunParameters = rule.runParameters && Object.keys(rule.runParameters).map(p => ({
						Name: p,
						Value: rule.runParameters[p]
					})) || [];
					return Data.postExternalApi(`EventRule/${method}`, {
						Oid: rule.id || null,
						Name: rule.name,
						DynamicDatasetId: rule.dataSet,
						ExternalServiceId: rule.externalServiceId,
						JsonCriteria: JSON.stringify(rule.criteria),
						RunParameters
					}).then((r) => {
						if (!rule.id) {
							rule.id = r.Data.Oid;
						}
						if (__DEBUG__)
							console.debug(method, { r, id: rule.id, Oid: r.Data.Oid });
							
						return rule;
					}).finally(() => {
						rule._isLoading = false;

						if (callback)
							callback(rule);
					});
				}
			},
			removeEventRule(rule) {
				rule._isLoading = true;
				return Data.getExternalApi('EventRule/DeleteEventRule', `eventRuleId=${rule.id}`).then((r) => {
					this.$notifySuccess(this.$tCaption('Rule removed'));
					return r;
				}).finally(() => {
					if (this._isDestroyed) return;
					if (rule) rule._isLoading = false;
					if (this.$refs.listView)
						this.$refs.listView.reload();
				});
			},
			onFileUpload(v) {
				var reader = new FileReader();
				reader.onload = () => {
					var dataUrl = reader.result;
					var base64 = dataUrl.split(',')[1];
					if (__DEBUG__)
						console.debug('Start excel convertion', { v, base64 });
					Data.postMethod('excelToJson', {
						data: base64
					}).then(r => {
						if (__DEBUG__)
							console.debug('Excel converted', r);
						return Data.postExternalApi('DynamicDataset/CreateUpdateDynamicDataset', {
							Oid: null,
							Name: this.dataSetName || 'Data set',
							JsonData: JSON.stringify(r)
						}).then((r) => {
							if (__DEBUG__)
								console.debug('CreateUpdateDynamicDataset', { r });
							var dataSet = { ...r, name: 'Data set', oid: r.Oid };
							this.$notifySuccess(this.$tCaption('Data set uploaded'));
							return dataSet;
						});
					}).catch((msg) => {
						this.$notifyError(`${this.$tCaption('Data set was not uploaded correctly')}. ${msg || ''}`);
					});
				};
				reader.readAsDataURL(v[0]);
			},
			setCriteria(rule, service) {
				this.currentEventRule = null;
				this.currentEventRuleService = service;
				this.criteriaPopupVisible = false;
				setImmediate(() => {
					if (this._isDestroyed) return;
					this.currentEventRule = rule;
					if (this.isDatasetRule(rule)) {
						this.$Data.getDynamicDatasets(null, null, { byKey: rule.dataSet }).then(r => {
							this.currentEventRuleDataSet = r.Results[0] && JSON.parse(r.Results[0].JsonData || '{}');
							this.criteriaPopupVisible = true;
						});
					} else {
						this.criteriaPopupVisible = true;
					}
				});
			},
			loadRules(pageIndex, limit, opts) {
				return Data.getRules(pageIndex, limit, opts, true);
			},
			onCreateEventRule(e) {
				if (__DEBUG__)
					console.debug('OnCreateEventRule', e);
				var rule = {
					id: null,
					name: this.$tCaption('New event rule'),
					_isLoading: false,
					type: e.itemData.id
				};
				this.newRule = rule;
				this.updateCreateEventRule(rule);
			},
			onExternalServiceCriteriaChange(criteria, params) {
				if (this.currentEventRule) {
					this.currentEventRule.criteria = criteria;
					this.currentEventRule.runParameters = params;
				}
			}
		}
	};
</script>
<style lang='scss'>
	.schedule-event-rules {
		@import "main/themes/generated/variables.base.scss";

		.event-rule-card {
			flex-basis: 100% !important;
			background: #fbfbfb !important;
			border-left: 4px solid $base-accent;
			margin: 1px !important;
			padding: 4px;
		}
	}
</style>
